// composables/useFeature.js
import { computed } from 'vue'
import { useDevice } from './useDevice'
import {
  FEATURES,
  PARENT_TO_CHILDREN_MAP,
  getEffectivePermissions,
} from '@/config/deviceFeatures'
import { useAppStore } from '@/stores'

export function useFeature(module) {
  const appStore = useAppStore()
  const { isDesktop } = useDevice()

  const currentModule = module || 'sale'

  const hasPermission = (requiredPerm) => {
    if (!requiredPerm) return true

    const perms = appStore.userPermissions || []

    // 直接拥有
    if (perms.includes(requiredPerm)) return true

    // 通过父级继承
    for (const userPerm of perms) {
      const children = PARENT_TO_CHILDREN_MAP[userPerm] || []
      if (children.includes(requiredPerm)) {
        return true
      }
    }

    return false
  }

  // ============================================================
  // ✅ hasAnyPermission - 检查任意一个权限
  // ============================================================
  const hasAnyPermission = (perms) => {
    if (!perms || !Array.isArray(perms) || perms.length === 0) return true
    return perms.some(p => hasPermission(p))
  }

  // ============================================================
  // ✅ hasAllPermissions - 检查所有权限
  // ============================================================
  const hasAllPermissions = (perms) => {
    if (!perms || !Array.isArray(perms) || perms.length === 0) return true
    return perms.every(p => hasPermission(p))
  }

  const features = computed(() => {
    const moduleFeatures = FEATURES[currentModule] || {}
    const result = {}

    for (const [featureKey, config] of Object.entries(moduleFeatures)) {
      // 跳过配置对象
      if (['columns', 'displayFields', 'card'].includes(featureKey)) {
        result[featureKey] = config
        continue
      }

      // 设备检查
      const deviceValue = isDesktop.value ? config.desktop : config.mobile
      if (!deviceValue) {
        result[featureKey] = false
        continue
      }

      // 从 config 中读取权限
      const requiredPerm = config._permission
      const hasPerm = hasPermission(requiredPerm)

      result[featureKey] = hasPerm
    }

    return result
  })

  const can = (featureKey) => {
    return features.value[featureKey] === true
  }

  // ============================================================
  // ✅ canOr - 检查任意一个功能权限（传入数组）
  // ============================================================
  const canOr = (featureKeys) => {
    if (!featureKeys || !Array.isArray(featureKeys) || featureKeys.length === 0) return true
    return featureKeys.some(key => can(key))
  }

  // ============================================================
  // ✅ canAnd - 检查所有功能权限（传入数组）
  // ============================================================
  const canAnd = (featureKeys) => {
    if (!featureKeys || !Array.isArray(featureKeys) || featureKeys.length === 0) return true
    return featureKeys.every(key => can(key))
  }

  const allEffectivePermissions = computed(() => {
    return getEffectivePermissions(appStore.userPermissions)
  })

  const hasDirectPermission = (perm) => {
    return (appStore.userPermissions || []).includes(perm)
  }

  const getChildren = (parentPerm) => {
    return PARENT_TO_CHILDREN_MAP[parentPerm] || []
  }

  return {
    features,
    isDesktop,
    can,
    canOr,
    canAnd,
    hasPermission,
    hasDirectPermission,
    hasAnyPermission,
    hasAllPermissions,
    allEffectivePermissions,
    getChildren,
  }
}